import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import useBundleTranslation from 'i18n';
import { getPageFromStorage, ICopyBuffer, IPage, setPageToStorage } from 'app/app/editor/index.functions';
import { AppBuilderContext } from 'app/app/editor/context';
import { merge } from 'ts-deepmerge';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import { APIResponse } from 'tools/types';
import { IMenuSection } from 'app/app/editor/tabs/components';
import { AxiosResponse } from 'axios';
import useAppEditorChannel, { IBroadcastMessage } from 'app/app/editor/broadcast/useAppEditorChannel';
import insertComponentIntoPage from 'app/app/editor/page/insertComponentIntoPage';
import AppBuilderRightPanel from 'app/app/editor/parts/AppBuilderRightPanel';
import { getNodeByUid } from 'app/app/editor/page/getNodeByUid';
import { useMIState } from 'app/app/editor/useMIState';
import useHistory, { IRawHistoryRecord } from 'app/app/editor/useHistory';
import { useUserInfo } from 'hooks/useUserPrivilege';
import { LayoutsPopup } from './tabs/elements/layout/layouts-popup';
import { formAPI } from 'api/form';
import { getDefaultsFields } from './page';

type AppEditorProps = {
    title: string;
    pageSession: string;
    internalName: string;
    withTemplate: boolean;
    isTemplate: boolean;
    historyLog: Array<Array<IRawHistoryRecord>>;
};

const AppEditor = ({ props }: { props: AppEditorProps }) => {
    const { t } = useBundleTranslation(['app/portal-page-builder']);
    const [activeTab, setActiveTab] = useState('elements');
    const [rightPanelState, setRightPanelState] = useState<string>('');
    const [storageKey, setStorageKey] = useState('mi-app-' + props.internalName + '-' + props.pageSession);
    const [componentUid, setComponentUid] = useState<string | null>(null);
    const [componentQuerySelector, setComponentQuerySelector] = useState<string | null>(null);
    const [page, setPageState] = useState<IPage>(getPageFromStorage(storageKey));
    const { message, sendMessage } = useAppEditorChannel('mi-app-' + props.internalName + '-' + props.pageSession);

    const withTemplate = props.withTemplate;
    const isTemplate = props.isTemplate;
    const state = useMIState();
    const history = useHistory(props.historyLog);

    const [copyBuffer, setCopyBuffer] = useState<ICopyBuffer>({ node: null, cut: false });
    const userInfo = useUserInfo();
    let setPage = useCallback(
        (data: any, mergeObject?: boolean) => {
            let newPage = getPageFromStorage(storageKey);
            if (null === newPage) {
                newPage = {};
            }
            newPage = false === mergeObject ? data : merge(newPage, data);
            setPageToStorage(storageKey, newPage);
            setPageState(newPage);
            history.addHistoryRecord(page, userInfo.display_name);
        },
        [props, message],
    );

    const isNewApp = useMemo(() => {
        return history.historyList.length === 0 && !withTemplate && page.layout.elements?.length === 0;
    }, [history, withTemplate, page.layout.elements]);

    const loadComponentData = async (type: string) => {
        const [formResult, layoutResult] = await Promise.allSettled([
            formAPI.load(`/data/app/components/${type}/form.json`),
            formAPI.load(`/data/app/components/${type}/layout.json`),
        ]);

        return {
            form: formResult.status === 'fulfilled' ? formResult.value?.data : undefined,
            layout: layoutResult.status === 'fulfilled' ? layoutResult.value?.data : undefined,
        };
    };

    const dropComponent = (message: IBroadcastMessage, layout?: any, form?: any) => {
        const { newPage, uid } = insertComponentIntoPage(
            page,
            message.data.data.type,
            message.data.target.dropPath,
            message.data.target.componentAfterId,
            layout,
        );

        if (form?.fields && newPage.components[uid]) {
            newPage.components[uid].settings = getDefaultsFields(form.fields);
        }

        setPage(newPage, false);
        sendMessage({ name: 'reloadPage' });

        const node = getNodeByUid(newPage.layout, uid);
        if (node && (node.type === 'component' || node.type === 'container')) {
            sendMessage({
                name: 'editComponent',
                data: { uid, querySelector: null },
            });
            setComponentUid(uid);
            setActiveTab('elements');
        }
    };

    useEffect(() => {
        if (message) {
            if (message.name === 'dropComponent') {
                loadComponentData(message.data.data.type)
                    .then(({ form, layout }) => {
                        dropComponent(message, layout, form);
                    })
                    .catch((error) => {
                        dropComponent(message);
                    });
            } else if (message.name == 'editComponent') {
                setComponentUid(message.data.uid);
                setComponentQuerySelector(message.data.querySelector);
                setActiveTab('elements');
            } else if (message.name == 'changeComponentSettings') {
                const uid = message.data.uid;
                const newPage = structuredClone(page);
                if (!newPage.components[uid]) {
                    return;
                }
                newPage.components[uid].settings = {
                    ...newPage.components[uid].settings,
                    ...message.data.settings,
                };
                setPage(newPage, false);
                sendMessage({
                    name: 'updateComponent',
                    data: { uid: uid, updateType: 'settings' },
                    broadcast: false,
                });
                if (componentUid == uid) {
                    setComponentUid(null);
                    setTimeout(() => {
                        setComponentUid(uid);
                    }, 10);
                }
            } else if (message.name == 'changeContainerSize') {
                const newPage = structuredClone(page);
                const node =
                    message.data.uid == 'page' ? newPage.layout : getNodeByUid(newPage.layout, message.data.uid);

                if (node) {
                    const element: any = document.querySelector('[data-uid="' + node.params.uid + '"]');
                    let sizeTypeWidth;
                    let sizeTypeHeight;
                    let widthValue = message.data.width + 'px';
                    let heightValue = message.data.height + 'px';
                    if (element) {
                        sizeTypeWidth = element.getAttribute('data-size-type-width') ?? 'fixed';
                        sizeTypeHeight = element.getAttribute('data-size-type-height') ?? 'fixed';
                    }
                    if (sizeTypeWidth != 'relative') sizeTypeWidth = 'fixed';
                    if (sizeTypeHeight != 'relative') sizeTypeHeight = 'fixed';

                    if (sizeTypeWidth == 'relative' && element && element.parentElement) {
                        widthValue =
                            Math.floor(
                                (message.data.width / element.parentElement.getBoundingClientRect().width) * 100,
                            ) + '%';
                        element.style.width = widthValue;
                    }
                    if (sizeTypeHeight == 'relative' && element && element.parentElement) {
                        heightValue =
                            Math.floor(
                                (message.data.height / element.parentElement.getBoundingClientRect().height) * 100,
                            ) + '%';
                        if (node?.subtype != 'page_menu') {
                            element.style.height = heightValue;
                        }
                    }

                    if (!node.params.layout) {
                        node.params.layout = {};
                    }
                    if (!node.params.layout.styles) {
                        node.params.layout.styles = {};
                    }

                    node.params.layout.styles['size-type-width'] = sizeTypeWidth;
                    node.params.layout.styles['size-type-height'] = sizeTypeHeight;
                    if (element) {
                        element.setAttribute('data-size-type-width', sizeTypeWidth);
                        element.setAttribute('data-size-type-height', sizeTypeHeight);
                    }

                    node.params.layout.styles.width = widthValue;
                    if (node?.subtype != 'page_menu') {
                        node.params.layout.styles.height = heightValue;
                    }

                    setPage(newPage, false);
                }
            } else if (message.name == 'showHistory') {
                if (message.data == null) {
                    history.selectHistoryRecord(0);
                    sendMessage({ name: 'showHistory', data: null });
                }
            }
        }
    }, [message]);

    const editComponent = useCallback(
        (data: any) => {
            if (data?.querySelector == componentQuerySelector && data?.uid == componentUid) {
                // If the item is already selected, deselect it.
                setComponentUid(null);
                setComponentQuerySelector(null);
                sendMessage({ name: 'editComponent', data: { uid: null, querySelector: null } });
            } else {
                setComponentUid(data?.uid ?? null);
                setComponentQuerySelector(data?.querySelector ?? null);
                sendMessage({ name: 'editComponent', data: data });
            }
        },
        [componentUid, componentQuerySelector],
    );

    useEffect(() => {
        if (componentUid !== null) {
            setRightPanelState('settings');
        }
    }, [componentUid]);

    useEffect(() => {
        if (componentUid && rightPanelState != 'settings') {
            setComponentUid(null);
            sendMessage({ name: 'editComponent', data: { uid: null, querySelector: null } });
        }
    }, [rightPanelState]);

    const { data: componentsMenu } = useQuery<Array<IMenuSection>, Error>({
        queryKey: ['portalPageEditor_componentsMenu'],
        queryFn: async () => {
            const response: AxiosResponse<APIResponse<Array<IMenuSection>>, any> = await instance.get(
                '/data/app/components-menu',
            );
            return response.data.status == 'OK' ? response.data.data : [];
        },
    });

    const setLayout = (id: number | null) => {
        if (id) {
            fetch(`/json/builder/layouts/layout-${id}.json`)
                .then((r) => r.json())
                .then((layout) => {
                    setPage(layout, false);
                    sendMessage({ name: 'reloadPage' });
                });
        }
    };

    if (!componentsMenu) {
        return null;
    }

    return (
        <>
            <AppBuilderContext.Provider
                value={{
                    setComponentUid,
                    setComponentQuerySelector,
                    componentQuerySelector,
                    editComponent,
                    sendMessage,
                    storageKey,
                    withTemplate,
                    page,
                    setPage,
                    componentsMenu,
                    state,
                    history,
                    copyBuffer,
                    setCopyBuffer,
                    isTemplate,
                    pageInternalName: props.internalName,
                    pageSession: props.pageSession,
                }}
            >
                <LayoutsPopup setLayout={setLayout} show={isNewApp}></LayoutsPopup>
                <AppBuilderRightPanel
                    internalName={props.internalName}
                    rightPanelState={rightPanelState}
                    setRightPanelState={setRightPanelState}
                    componentUid={componentUid}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
            </AppBuilderContext.Provider>
        </>
    );
};

export default AppEditor;
